import React, { useState } from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  CircularProgress,
} from '@mui/material';
import { useQuery } from 'react-query';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { tagsAPI } from '../../services/api';

function TagSelector({ control, name, disabled = false, label }) {
  const { t } = useTranslation();
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const displayLabel = label || t('tagSelector.label');

  // Recherche de tags
  const { data: tagsData, isLoading: tagsLoading } = useQuery(
    ['tags-list', tagSearchTerm],
    () => tagsAPI.search(tagSearchTerm).then(res => res.data.data),
    {
      enabled: tagSearchTerm.length >= 2,
      staleTime: 2 * 60 * 1000,
      cacheTime: 5 * 60 * 1000
    }
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
          <Autocomplete
            {...field}
            multiple
            freeSolo
            options={tagsData || []}
            getOptionLabel={(option) => {
              // Pour les options d'ajout
              if (option.isAddOption) {
                return t('tagSelector.addOption', { value: option.inputValue });
              }
              // Pour les nouvelles valeurs string
              if (typeof option === 'string') return option;
              // Pour les objets avec displayName
              if (option.displayName) return option.displayName;
              // Pour les tags existants
              return option.nom || '';
            }}
            isOptionEqualToValue={(option, value) => {
              if (typeof option === 'object' && typeof value === 'object') {
                if (option.id && value.id) return option.id === value.id;
                if (option.isNew && value.isNew) return option.displayName === value.displayName;
              }
              return false;
            }}
            filterOptions={(options, params) => {
              const filtered = options.filter(option => {
                return option.nom.toLowerCase().includes(params.inputValue.toLowerCase());
              });

              const { inputValue } = params;
              // Suggérer l'ajout d'un nouveau tag si la saisie ne correspond à aucun existant
              const isExisting = options.some(option => 
                option.nom.toLowerCase() === inputValue.toLowerCase()
              );
              
              if (inputValue !== '' && !isExisting) {
                filtered.push({
                  inputValue,
                  nom: inputValue,
                  displayName: inputValue,
                  isNew: true,
                  isAddOption: true
                });
              }

              return filtered;
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option.id || option.displayName || index}
                  variant={option.isNew ? "filled" : "outlined"}
                  color={option.isNew ? "secondary" : "default"}
                  label={
                    typeof option === 'string' ? option :
                    option.displayName ? 
                      (option.isNew ? `${option.displayName} (${t('tagSelector.new')})` : option.displayName) :
                    option.nom || ''
                  }
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={displayLabel}
                placeholder={tagsLoading ? t('tagSelector.loading') : t('tagSelector.placeholder')}
                margin="none"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {tagsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            onChange={(event, newValue, reason, details) => {
              console.log('🔄 TagSelector onChange:', { newValue, reason, details });
              
              // Si on ajoute par freeSolo (entrée au clavier)
              if (reason === 'createOption' && details?.option && typeof details.option === 'string') {
                const inputValue = details.option.trim();
                
                const newTag = {
                  isNew: true,
                  nom: inputValue,
                  displayName: inputValue
                };
                
                const updatedValue = [...(field.value || []), newTag];
                field.onChange(updatedValue);
                console.log('✅ Nouveau tag ajouté via freeSolo:', newTag);
                return;
              }
              
              // Traiter les autres cas
              const processedTags = newValue.map(option => {
                if (typeof option === 'string') {
                  // Nouveau tag saisi directement
                  return {
                    isNew: true,
                    nom: option.trim(),
                    displayName: option.trim()
                  };
                } else if (typeof option === 'object') {
                  if (option.id) {
                    // Tag existant
                    return option;
                  } else if (option.isNew) {
                    // Nouveau tag via l'option "Ajouter"
                    return option;
                  }
                }
                return null;
              }).filter(Boolean);
              
              field.onChange(processedTags);
              console.log('✅ Tags sélectionnés:', processedTags);
            }}
            onInputChange={(event, newInputValue) => {
              setTagSearchTerm(newInputValue);
            }}
            inputValue={tagSearchTerm}
            disabled={disabled}
          />
      )}
    />
  );
}

export default TagSelector;
